import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  TreePine, 
  Flower2, 
  Armchair, 
  ChefHat,
  MapPin,
  Sun
} from 'lucide-react';

const GardenSection = () => {
  const gardenFeatures = [
    {
      icon: TreePine,
      title: "Jardin paysager",
      description: "1000m² d'espaces verts entretenus"
    },
    {
      icon: Armchair,
      title: "Espaces détente",
      description: "Mobilier de jardin et zones ombragées"
    },
    {
      icon: ChefHat,
      title: "Espace barbecue",
      description: "Plancha et barbecue à disposition"
    },
    {
      icon: Sun,
      title: "Exposition idéale",
      description: "Ensoleillé toute la journée"
    }
  ];

  const gardenImages = [
    {
      id: 1,
      url: "https://customer-assets.emergentagent.com/job_curist-apartments/artifacts/ksk0d2hb_92f59eba-2695-4554-85bf-761fac268f00.jpeg",
      title: "Terrasse aménagée",
      description: "Coin repas avec vue sur le jardin"
    },
    {
      id: 2,
      url: "https://customer-assets.emergentagent.com/job_curist-apartments/artifacts/ghh61dek_75898d81-78d3-4fde-91d9-92f9e479d45d.jpeg",
      title: "Façade Les Platanes",
      description: "Architecture provençale au cœur du jardin"
    },
    {
      id: 3,
      url: "https://customer-assets.emergentagent.com/job_curist-apartments/artifacts/v8z5j41f_407749fa-3c0f-4e4b-87a6-34079b9c6c93.jpeg",
      title: "Espace barbecue",
      description: "Zone de convivialité sous les arbres"
    },
    {
      id: 4,
      url: "https://customer-assets.emergentagent.com/job_curist-apartments/artifacts/gltmtsyi_73d80f35-ed75-4ddf-9f88-5daee66e9e25.jpeg",
      title: "Salon de jardin",
      description: "Mobilier en fer forgé dans un cadre verdoyant"
    },
    {
      id: 5,
      url: "https://customer-assets.emergentagent.com/job_curist-apartments/artifacts/ftnlm59u_IMG_5783.jpeg",
      title: "Jardin méditerranéen",
      description: "Végétation locale et espaces aménagés"
    },
    {
      id: 6,
      url: "https://customer-assets.emergentagent.com/job_curist-apartments/artifacts/y61e001a_IMG_5782.jpeg",
      title: "Allée jardinée",
      description: "Cheminements ombragés entre palmiers et végétation"
    },
    {
      id: 7,
      url: "https://customer-assets.emergentagent.com/job_curist-apartments/artifacts/b5h2gosb_IMG_4037.jpeg",
      title: "Espace détente",
      description: "Coin tranquille pour se reposer au frais"
    },
    {
      id: 8,
      url: "https://customer-assets.emergentagent.com/job_curist-apartments/artifacts/0bof1lvk_e25f3eb0-1108-47da-926b-a062aff2acf5.jpeg",
      title: "Vue panoramique",
      description: "Perspective sur l'ensemble du jardin paysager"
    },
    {
      id: 9,
      url: "https://customer-assets.emergentagent.com/job_curist-apartments/artifacts/wgt3sov7_IMG_5784.jpeg",
      title: "Espaces verts",
      description: "Pelouses et massifs entretenus avec soin"
    }
  ];

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="garden" className="py-24 bg-gradient-to-b from-green-50 to-emerald-50">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          
          {/* En-tête de section */}
          <div className="text-center mb-16">
            <Badge variant="secondary" className="px-4 py-2 text-sm font-medium bg-emerald-100 text-emerald-800 border-emerald-200 mb-4">
              <Flower2 className="h-4 w-4 mr-2" />
              Notre jardin
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Un écrin de 
              <span className="bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent"> verdure</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Profitez de notre magnifique jardin paysager de 1000m² avec ses espaces de détente, son mobilier de jardin et son espace barbecue. Un havre de paix au cœur de Lamalou-les-Bains.
            </p>
          </div>

          {/* Caractéristiques du jardin */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {gardenFeatures.map((feature, index) => (
              <Card key={index} className="text-center border-emerald-200 hover:shadow-lg transition-all duration-300 hover:border-emerald-300">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="h-6 w-6 text-emerald-700" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Galerie photo du jardin */}
          <div className="mb-12">
            <h3 className="text-3xl font-bold text-gray-900 text-center mb-8">
              Découvrez nos espaces extérieurs
            </h3>
            
            {/* Grille de photos */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
              {gardenImages.map((image, index) => (
                <Card key={image.id} className="group overflow-hidden border-emerald-200 hover:shadow-xl transition-all duration-500 hover:border-emerald-300">
                  <div className="relative overflow-hidden">
                    <img 
                      src={image.url} 
                      alt={image.title}
                      className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <h4 className="font-bold text-lg mb-1">{image.title}</h4>
                      <p className="text-sm text-gray-200">{image.description}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Section avantages */}
          <Card className="bg-gradient-to-r from-emerald-600 to-green-600 text-white border-0 shadow-2xl">
            <CardContent className="p-8 md:p-12 text-center">
              <div className="max-w-3xl mx-auto">
                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  Un cadre exceptionnel pour votre séjour
                </h3>
                <p className="text-emerald-100 text-lg mb-8 leading-relaxed">
                  Notre jardin de 1000m² vous offre un véritable havre de paix avec ses espaces ombragés, 
                  son mobilier de jardin et son espace barbecue. Idéal pour se détendre après une séance de cure 
                  ou pour partager des moments conviviaux en famille ou entre amis.
                </p>
                <div className="flex flex-wrap justify-center gap-4 text-sm">
                  <div className="flex items-center bg-white/20 rounded-full px-4 py-2">
                    <MapPin className="h-4 w-4 mr-2" />
                    <span>Accès direct depuis tous les appartements</span>
                  </div>
                  <div className="flex items-center bg-white/20 rounded-full px-4 py-2">
                    <TreePine className="h-4 w-4 mr-2" />
                    <span>Espaces ombragés et ensoleillés</span>
                  </div>
                  <div className="flex items-center bg-white/20 rounded-full px-4 py-2">
                    <ChefHat className="h-4 w-4 mr-2" />
                    <span>Barbecue et plancha inclus</span>
                  </div>
                </div>
                
                <div className="mt-8">
                  <Button 
                    onClick={scrollToContact}
                    className="bg-white text-emerald-700 hover:bg-gray-50 text-lg px-8 py-3 font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    Réserver votre séjour
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default GardenSection;